import React, { useMemo, useState } from 'react';
import styles from './styles.module.css';
import { Icon, IconType } from '@/ui';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import CheckBox from '@/ui/CheckBox/CheckBox';
import useOutsideClick from '@/hooks/useClickOutside';

export type NestedOption = {
  id: string;
  label: string;
};
export type Option = {
  id: string;
  label: string;
  value: string;
  nestedOption?: NestedOption[] | [];
};
type Props = {
  text: string;
  emptyText?: string;
  options: Option[];
  selected: Option[];
  onChange: (newSelected: Option[]) => void;
  withIcon?: boolean;
  disabled?: boolean;
  label?: string;
  withArrow?: boolean;
};
export const CheckBoxSelect = ({
  options,
  selected,
  onChange,
  text,
  label,
  withIcon = true,
  disabled,
  withArrow,
  emptyText,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const ref = useOutsideClick<HTMLDivElement>(closeSelect, false);
  const isAllSelected = options.length === selected?.length;
  const selectorStyles = classNames(styles.selector, {
    [styles.active]: isOpen,
    [styles.disabled]: disabled,
  });

  const handleSelectAll = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      if (options.length > 0) onChange(options?.map((opt: Option) => opt));
    }
  };

  const handleSelectOption = (option: Option) => {
    if (isOptionSelected(option)) {
      onChange(selected.filter((opt: Option) => opt?.id !== option.id));
    } else {
      onChange([...selected, option]);
    }
  };

  const handleSelectNestedOption = (nested: NestedOption, option: Option) => {
    const selectedOption = isOptionSelected(option);
    const nestedSelected = isNestedSelected(nested, option);

    if (nestedSelected) {
      // If the nested option is already selected, deselect it
      const newSelected = selected
        .map((opt: Option) => {
          if (opt.id === option.id) {
            return {
              ...opt,
              nestedOption: opt?.nestedOption?.filter((st) => st.id !== nested.id),
            };
          }
          return opt;
        })
        .filter((opt) => opt.nestedOption?.length !== 0);

      onChange(newSelected);
    } else {
      // If the nested option is not selected, select it
      const newNestedOption = selectedOption
        ? [...(selectedOption.nestedOption || []), nested]
        : [nested];
      const newSelected = selected.map((opt: Option) => {
        if (opt.id === option.id) {
          return {
            ...opt,
            nestedOption: newNestedOption,
          };
        }
        return opt;
      });

      if (!selectedOption) {
        newSelected.push({ ...option, nestedOption: newNestedOption });
      }

      onChange(newSelected);
    }
  };

  const isNestedSelected = (nestedOption: NestedOption, outlet: Option) => {
    const optionSelected = selected.find((opt: Option) => opt?.id === outlet?.id);
    if (optionSelected) {
      return !!optionSelected?.nestedOption?.find(
        (nested: NestedOption) => nested.id === nestedOption.id,
      );
    }
    return false;
  };

  const isOptionSelected = (option: Option) => {
    return selected.find((item: Option) => item?.id === option.id);
  };

  function toggleDropdown() {
    setIsOpen(!isOpen);
  }

  function closeSelect() {
    setIsOpen(false);
  }
  const placeholderText = useMemo(() => {
    if (selected.length === 0) {
      return emptyText || 'Select';
    }
    return selected.length === options?.length
      ? text
      : selected.map((item) => item.label).join(', ');
  }, [selected, options, text, emptyText]);

  return (
    <div className={styles.container} ref={ref}>
      {label && <span className={styles.label}>{label}</span>}
      <div className={selectorStyles} onClick={toggleDropdown}>
        {withIcon && <Icon type={IconType.Filter} width={24} height={24} fill='transparent' />}
        <p className={styles.text}>{placeholderText}</p>
        {withArrow && (
          <div className={styles.arrowContainer}>
            {isOpen ? (
              <Icon
                type={IconType.Close}
                width='20'
                height='16'
                fill='var(--oliver-placeholder)'
                stroke={'transparent'}
              />
            ) : (
              <Icon
                type={IconType.DropDownIcon}
                width='24'
                height='24'
                fill='transparent'
                stroke='var(--oliver-placeholder)'
              />
            )}
          </div>
        )}
      </div>
      {isOpen && !disabled && (
        <ul className={styles.dropdown}>
          <li className={`${styles.option} ${styles.selectAll}`} onClick={handleSelectAll}>
            <CheckBox id={'all'} isChecked={isAllSelected} label={t('Shared.select_all')} />
          </li>
          {options.map((option: Option, index) => (
            <React.Fragment key={option.value}>
              <li
                key={option.value}
                className={`${styles.option} ${styles.outletOption}`}
                onClick={() => handleSelectOption(option)}
              >
                <CheckBox
                  id={index.toString()}
                  isChecked={!!isOptionSelected(option)}
                  label={option.label}
                />
              </li>
              {option?.nestedOption &&
                option.nestedOption.length > 0 &&
                option.nestedOption.map((nestedOption: NestedOption, nestedIndex) => (
                  <li
                    key={nestedOption.id}
                    className={`${styles.option} ${styles.stationOption}`}
                    onClick={() => handleSelectNestedOption(nestedOption, option)}
                  >
                    <CheckBox
                      id={`${index}-${nestedIndex}`}
                      isChecked={isNestedSelected(nestedOption, option)}
                      label={nestedOption.label}
                    />
                  </li>
                ))}
            </React.Fragment>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CheckBoxSelect;
